<template>
  <v-card
    v-if="fileNames && fileNames.length > 0"
    :elevation="editMode ? 1 : 0"
    @mouseenter.capture="showPauseBtn = true"
    @mouseleave.capture="showPauseBtn = false"
  >
    <v-carousel
      v-model="activeImage"
      :cycle="isCycle"
      :height="height"
      hide-delimiter-background
      :hide-delimiters="hideDelimiters"
      :show-arrows-on-hover="editMode ? false : true"
      :show-arrows="fileNames.length > 1"
      style="position: relative"
      ref="carousel"
    >
      <v-carousel-item v-for="(image, index) in images" :key="index">
        <v-img
          @mouseenter="showPauseBtn = true"
          @mouseleave="showPauseBtn = false"
          @click="$emit('imgClick')"
          :src="image.src"
          :alt="imageAlt"
          :eager="eager"
          :height="height"
          :width="width"
          :contain="contain"
          v-bind="imageProps"
          @error="(loading = false), loadErrors.push(index)"
          @load="loading = false"
          :aspect-ratio="aspectRatio"
          :max-height="maxHeight"
          :max-width="maxWidth"
          :min-width="minWidth"
          style="flex-grow: 0 0; background-color: #eee"
          position="center"
        >
          <slot v-if="loading">
            <div class="d-flex scoped" style="height: 100%">
              <v-skeleton-loader
                type="image"
                height="100%"
                width="100%"
                class=""
              />
            </div>
          </slot>

          <slot
            v-else-if="!loading && loadErrors.includes(index)"
            name="loadError"
          >
            <v-sheet
              color="grey lighten-3"
              :height="height"
              :width="width"
              style="position: absolute; top: 0; left: 0: z-index: 0; width: 100%;"
            >
              <v-row
                no-gutters
                justify="center"
                align="center"
                class="fill-height"
              >
                <v-col cols="auto">
                  <v-icon x-large color="grey">
                    {{ icons.mdiImageMultiple }}
                  </v-icon>
                </v-col>
              </v-row>
            </v-sheet>
          </slot>
        </v-img>
      </v-carousel-item>

      <!-- Pause button -->
      <div
        v-if="showPauseBtn && fileNames.length > 1"
        @mouseenter="showPauseBtn = true"
        @mouseleave="showPauseBtn = false"
        style="position: absolute; top: 0; left: 0; border-radius: 50%; background-color: #000; opacity: 40%; z-index: 99"
        :style="[
          { top: pauseBtnPosition.top },
          { left: pauseBtnPosition.left }
        ]"
        class="pa-3"
      >
        <v-btn @click="isCycle = !isCycle" icon
          ><v-icon size="48">{{
            isCycle ? icons.mdiPause : icons.mdiPlay
          }}</v-icon></v-btn
        >
      </div>
    </v-carousel>

    <v-row
      v-if="editMode"
      class="ma-0 pa-2"
      :justify="showTextField ? 'space-between' : 'end'"
      align="center"
    >
      <v-col class="pa-0" cols="8">
        <v-text-field
          v-if="showTextField"
          v-model="fieldText"
          height="20px"
          dense
          solo
          :hide-details="true"
          :placeholder="`${$t('typeSmallDescription')}`"
          name="imageDescription"
        >
        </v-text-field>
      </v-col>

      <v-btn
        class="cursor-pointer"
        @click="$emit('delete-image', images[activeImage].name)"
        color="error lighten-1"
        x-small
        outlined
      >
        <v-icon color="error lighten-1" class="mr-1" x-small>{{
          icons.mdiDelete
        }}</v-icon>
        <span>{{ $t('deleteImage') }}</span>
      </v-btn>
    </v-row>

    <v-row
      v-else-if="!editMode && fieldText"
      class="ma-0 pa-2"
      justify="center"
      align="center"
    >
      <span>{{ fieldText }}</span>
    </v-row>
  </v-card>

  <v-sheet
    v-else
    color="grey lighten-3"
    :height="errorSheetHeight"
    width="100%"
    style="width: 100%;"
  >
    <v-row no-gutters justify="center" align="center" class="fill-height">
      <v-col cols="auto">
        <img
          class="grey-scale"
          :src="require('@/assets/trust_logo.svg')"
          width="100px"
        />
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script>
import { mdiImageMultiple, mdiDelete, mdiPause, mdiPlay } from '@mdi/js';
import { VUE_APP_FILE_URL } from '@/utilities/constants.js';

export default {
  props: {
    type: {
      required: false,
      type: String
    },

    minWidth: {
      type: String,
      default: () => ''
    },

    fileNames: {
      required: false,
      default: () => []
    },

    profileId: {
      required: false,
      type: String
    },

    projectId: {
      required: false,
      type: String
    },

    itineraryId: {
      type: String,
      required: false
    },

    imageType: {
      required: false,
      type: String,
      default: 'n'
    },

    height: {
      required: false,
      type: String,
      default: '100%'
    },

    width: {
      required: false,
      type: String,
      default: '100%'
    },

    eager: {
      required: false,
      type: Boolean
    },

    imageProps: {
      type: Object,
      required: false,
      default: () => ({})
    },

    contain: {
      type: Boolean,
      default: () => true
    },

    maxHeight: {
      type: String,
      default: () => ''
    },

    maxWidth: {
      type: String,
      default: () => ''
    },

    imgSrc: {
      type: String,
      default: () => ''
    },

    useLazy: {
      type: Boolean,
      default: () => false
    },

    aspectRatio: {
      type: Number,
      required: false
    },

    cycle: {
      type: Boolean,
      default: () => true
    },

    editMode: {
      type: Boolean,
      default: () => false
    },

    showTextField: {
      type: Boolean,
      default: () => false
    },

    fileText: {
      type: String,
      default: ''
    },

    errorSheetHeight: {
      type: String,
      default: '220px'
    },

    hideDelimitersProp: {
      type: Boolean,
      default: false
    },

    imageAlt: {
      type: String,
      default: 'Trust image'
    }
  },

  data() {
    return {
      loadErrors: [],
      loading: true,
      images: [],
      activeImage: 0,
      fieldText: '',
      isCycle: true,
      pauseBtnPosition: { top: 0, left: 0 },
      showPauseBtn: false
    };
  },

  created() {
    this.setUrls('initial');
    this.fieldText = this.fileText;
    this.isCycle = this.cycle;
  },

  methods: {
    validateSrcProps() {
      const srcProps = ['type', 'imageType', 'fileName'];
      const idTypes = ['projectId', 'profileId', 'itineraryId'];

      const missingSrcProps = element => {
        if (!this[element]) return true;
        return false;
      };
      if (srcProps.some(missingSrcProps)) return false;

      const hasId = element => this[element];

      if (!idTypes.some(hasId)) return false;

      return true;
    },

    setUrls(type) {
      if (!this.fileNames || !this.fileNames.length) {
        this.loadErrors = [0];
        this.loading = false;
        return;
      }
      this.images = this.fileNames.map(file => {
        let id = '';
        switch (this.type) {
          case 'p':
            id = this.projectId;
            break;
          case 'u':
            id = this.profileId;
            break;
          default:
            break;
          case 'i':
            id = this.itineraryId;
            break;
        }

        if (file?.src) return { name: file.src, src: file.src };

        if (!this.type || !id || !this.imageType || !file)
          return {
            name: file,
            src: ''
          };

        return {
          name: file,
          src: `${VUE_APP_FILE_URL}/${this.type}/${id}/${this.imageType}/${file}`
        };
      });

      if (type === 'initial') return;

      this.activeImage = this.images.length - 1;
    },

    getPausePosition() {
      const height = this.$refs.carousel.$el.clientHeight;
      const width = this.$refs.carousel.$el.clientWidth;

      this.pauseBtnPosition = {
        left: `${(width - 60) / 2}px`,
        top: `${(height - 60) / 2}px`
      };
    }
  },

  computed: {
    hideDelimiters() {
      if (this.fileNames.length <= 1 || this.hideDelimitersProp) return true;
      return false;
    },

    icons: () => ({
      mdiImageMultiple,
      mdiDelete,
      mdiPause,
      mdiPlay
    })
  },

  watch: {
    fileNames: {
      handler: function(newVal) {
        this.setUrls('update');
      },
      deep: true
    },

    fieldText(newVal) {
      this.$emit('field-text-input', newVal);
    },

    loading(newVal) {
      if (newVal === false) {
        this.$nextTick(() => {
          this.getPausePosition();
        });
      }
    }
  }
};
</script>

<style>
.scoped > .v-skeleton-loader--is-loading > .v-skeleton-loader__image {
  min-height: 100%;
}

div > button.v-carousel__controls__item {
  background-color: var(--v-brand-base);
  opacity: 0.25;
  height: 5px;
}

div > button.v-carousel__controls__item.v-item--active {
  background-color: var(--v-brand-base);
  opacity: 0.7;
}
</style>
